import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import "../../styles/components/PostCaption.css";

function hasMeaningfulValue(value) {
  return typeof value === "string" && value.trim().length > 0;
}

export default function PostCaption({ text, t }) {
  const captionId = useId();
  const captionRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const readMoreLabel = t("read_more", { defaultValue: "Read more" });
  const showLessLabel = t("show_less", { defaultValue: "Show less" });
  const toggleLabel = expanded ? showLessLabel : readMoreLabel;

  useEffect(() => {
    setExpanded(false);
    setIsExpandable(false);
  }, [text]);

  const measureOverflow = useCallback(() => {
    const element = captionRef.current;
    if (!element) return;

    const computedStyle = window.getComputedStyle(element);
    const lineHeight = Number.parseFloat(computedStyle.lineHeight);
    const maxCollapsedHeight = Number.isFinite(lineHeight)
      ? lineHeight * 2
      : element.clientHeight;
    const nextIsExpandable = element.scrollHeight > maxCollapsedHeight + 1;

    setIsExpandable(nextIsExpandable);
    if (!nextIsExpandable && expanded) {
      setExpanded(false);
    }
  }, [expanded]);

  useEffect(() => {
    const requestFrame =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame
        : (callback) => window.setTimeout(callback, 0);
    const cancelFrame =
      typeof window.cancelAnimationFrame === "function"
        ? window.cancelAnimationFrame
        : window.clearTimeout;
    let frameId = null;

    const scheduleMeasure = () => {
      if (frameId !== null) {
        cancelFrame(frameId);
      }
      frameId = requestFrame(() => {
        frameId = null;
        measureOverflow();
      });
    };

    scheduleMeasure();

    const element = captionRef.current;
    const observedElement = element?.parentElement || element;
    const ResizeObserverConstructor = window.ResizeObserver;
    let resizeObserver = null;

    if (observedElement && typeof ResizeObserverConstructor === "function") {
      resizeObserver = new ResizeObserverConstructor(scheduleMeasure);
      resizeObserver.observe(observedElement);
      if (element && element !== observedElement) {
        resizeObserver.observe(element);
      }
    }

    window.addEventListener("resize", scheduleMeasure);

    return () => {
      if (frameId !== null) {
        cancelFrame(frameId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [measureOverflow, text]);

  if (!hasMeaningfulValue(text)) return null;

  return (
    <div
      className={[
        "rp-post__caption-wrap",
        isExpandable ? "rp-post__caption-wrap--expandable" : "",
        isExpandable && !expanded ? "rp-post__caption-wrap--collapsed" : "",
        expanded ? "rp-post__caption-wrap--expanded" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <p
        id={captionId}
        ref={captionRef}
        className={[
          "rp-post__caption",
          !expanded ? "rp-post__caption--clamped" : "",
          expanded ? "rp-post__caption--expanded" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {text}
      </p>
      {isExpandable ? (
        <button
          type="button"
          className="rp-post__caption-toggle"
          aria-controls={captionId}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {toggleLabel}
        </button>
      ) : null}
    </div>
  );
}
